import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import { getDailySales } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Input } from '../components/ui/input';
import { CalendarDays, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDailySalesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    setLoading(true);
    getDailySales(month)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Error al cargar ventas diarias'))
      .finally(() => setLoading(false));
  }, [month]);

  const formatDay = (iso) => {
    try {
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(y, m - 1, d);
      return dt.toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short' });
    } catch {
      return iso;
    }
  };

  const monthLabel = () => {
    const [y, m] = month.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1);
    return date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  };

  const branches = data?.branches || [];
  const days = data?.days || [];
  const totalsByBranch = data?.totals?.by_branch || {};
  const grandTotal = data?.totals?.grand_total || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="daily-sales-title">Ventas Diarias</h1>
            <p className="text-slate-600 mt-1 capitalize">Reporte por sucursal · {monthLabel()}</p>
          </div>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-auto"
            data-testid="daily-sales-month"
          />
        </div>

        <div className="card-base p-6" data-testid="finance-daily-sales">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-blue-600" />
              Ventas diarias por sucursal
            </h2>
            <span className="text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-1 rounded-full">
              Total mes: {formatCurrency(grandTotal)}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : days.length === 0 || branches.length === 0 ? (
            <p className="text-slate-400 text-center py-8" data-testid="daily-sales-empty">No hay ventas registradas este mes</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="table-header sticky left-0 bg-white">Fecha</th>
                    {branches.map((b) => (
                      <th key={b.id} className="table-header text-right">{b.name}</th>
                    ))}
                    <th className="table-header text-right bg-slate-50">Total día</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.date} className="table-row">
                      <td className="table-cell font-medium text-slate-800 capitalize sticky left-0 bg-white">
                        {formatDay(d.date)}
                      </td>
                      {branches.map((b) => {
                        const cell = d.by_branch?.[b.id];
                        return (
                          <td key={b.id} className="table-cell text-right tabular-nums">
                            {cell ? (
                              <div>
                                <div className="font-semibold text-slate-900">{formatCurrency(cell.total)}</div>
                                <div className="text-xs text-slate-500">{cell.orders} órd.</div>
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="table-cell text-right font-bold text-blue-700 tabular-nums bg-slate-50">
                        {formatCurrency(d.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                    <td className="table-cell font-bold text-slate-900 sticky left-0 bg-slate-50">Total mes</td>
                    {branches.map((b) => (
                      <td key={b.id} className="table-cell text-right font-bold text-slate-900 tabular-nums">
                        {formatCurrency(totalsByBranch[b.id] || 0)}
                      </td>
                    ))}
                    <td className="table-cell text-right font-bold text-blue-700 tabular-nums">
                      {formatCurrency(grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
